import React, { useState, useEffect } from 'react';
import {
    Stack,
    Dropdown,
    IDropdownOption,
    Toggle,
    PrimaryButton,
    Spinner,
    MessageBar,
    MessageBarType,
    Text,
    SpinButton,
    DefaultButton,
    IconButton
} from '@fluentui/react';
import { ApiHelper, isApiHelperErrorResponse } from '../ApiHelper';
import ColumnListWithActions from './querybuilder/ColumnListWithActions.tsx';
import ResultsTable from './querybuilder/ResultsTable.tsx';

interface TableSchemaInfo {
    tableName: string;
    columns: ColumnSchemaInfo[];
}

interface ColumnSchemaInfo {
    columnName: string;
    dataType: string;
    isNullable: boolean;
    isPrimaryKey: boolean;
}

interface FilterDefinition {
    columnName: string;
    operator: number;
    value?: string;
    valueEnd?: string;
}

interface AggregationDefinition {
    columnName: string;
    function: number;
    alias?: string;
}

interface SortDefinition {
    columnName: string;
    direction: number;
}

interface QueryBuilderRequest {
    tableName: string;
    columns: string[];
    filters: FilterDefinition[];
    isAggregationMode: boolean;
    groupByColumns: string[];
    dateGroupings: Record<string, number>;
    aggregations: AggregationDefinition[];
    orderBy: SortDefinition[];
    maxRows: number;
}

interface QueryBuilderResponse {
    rows: Record<string, any>[];
    totalRows: number;
    isTruncated: boolean;
    columns: { name: string; dataType: string }[];
    generatedSql: string;
}

interface QueryBuilderError {
    message?: string;
    exception?: string;
    innerException?: string;
    details?: string;
    exceptionType?: string;
    sqlState?: string;
    errorNumber?: string | number;
}

const QueryBuilder: React.FC = () => {
    const [schema, setSchema] = useState<TableSchemaInfo[]>([]);
    const [selectedTable, setSelectedTable] = useState<string>('');
    const [selectedColumns, setSelectedColumns] = useState<string[]>([]);
    const [filters, setFilters] = useState<FilterDefinition[]>([]);
    const [isAggregationMode, setIsAggregationMode] = useState(false);
    const [groupByColumns, setGroupByColumns] = useState<string[]>([]);
    const [dateGroupings, setDateGroupings] = useState<Record<string, number>>({});
    const [aggregations, setAggregations] = useState<AggregationDefinition[]>([]);
    const [sortOrders, setSortOrders] = useState<SortDefinition[]>([]);
    const [results, setResults] = useState<QueryBuilderResponse | null>(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [maxRows, setMaxRows] = useState<number>(1000);
    const [showSql, setShowSql] = useState(false);
    const [isBuilderCollapsed, setIsBuilderCollapsed] = useState(false);

    // Load schema on mount
    useEffect(() => {
        loadSchema();
    }, []);

    const loadSchema = async () => {
        try {
            const data = await ApiHelper<TableSchemaInfo[]>('/api/querybuilder/schema', 'GET');
            console.log('Schema data received:', data);

            // Ensure data is an array
            if (Array.isArray(data)) {
                setSchema(data);
            } else {
                console.error('Schema data is not an array:', data);
                setSchema([]);
                setError('Invalid schema format received from server.');
            }
        } catch (err: any) {
            console.error('Error loading schema:', err);
            setError('Failed to load schema: ' + (err.message || 'Unknown error'));
            setSchema([]);
        }
    };

    const handleTableChange = (_: any, option?: IDropdownOption) => {
        if (option) {
            const tableName = option.key as string;
            setSelectedTable(tableName);

            // Select all columns by default
            const table = schema.find(t => t.tableName === tableName);
            if (table) {
                setSelectedColumns(table.columns.map(c => c.columnName));
            } else {
                setSelectedColumns([]);
            }

            setFilters([]);
            setGroupByColumns([]);
            setDateGroupings({});
            setAggregations([]);
            setSortOrders([]);
            setResults(null);
            setError(null);
        }
    };

    const handleAggregationModeChange = (_: any, checked?: boolean) => {
        setIsAggregationMode(!!checked);
        if (checked) {
            // Clear column selection when entering aggregation mode
            setSelectedColumns([]);
        } else {
            // Clear GROUP BY, date groupings, and aggregations when leaving aggregation mode
            setGroupByColumns([]);
            setDateGroupings({});
            setAggregations([]);
        }
        setResults(null);
    };

    const handleNewQuery = () => {
        // Reset all state to start a new query
        setSelectedTable('');
        setSelectedColumns([]);
        setFilters([]);
        setIsAggregationMode(false);
        setGroupByColumns([]);
        setDateGroupings({});
        setAggregations([]);
        setSortOrders([]);
        setResults(null);
        setError(null);
    };

    const handleExecuteQuery = async () => {
        setLoading(true);
        setError(null);
        setResults(null);

        const request: QueryBuilderRequest = {
            tableName: selectedTable,
            columns: isAggregationMode ? [] : selectedColumns,
            filters: filters,
            isAggregationMode: isAggregationMode,
            groupByColumns: isAggregationMode ? groupByColumns : [],
            dateGroupings: isAggregationMode ? dateGroupings : {},
            aggregations: isAggregationMode ? aggregations : [],
            orderBy: sortOrders,
            maxRows: maxRows
        };

        console.log('Executing query with request:', JSON.stringify(request, null, 2));

        try {
            const response = await ApiHelper<QueryBuilderResponse, QueryBuilderError>('/api/querybuilder/execute', 'POST', request, true);
            console.log('Query response:', response);

            // Check if response indicates an error
            if (isApiHelperErrorResponse<QueryBuilderError>(response)) {
                console.error('Response has error status:', response);
                const data = response.data;

                // Build comprehensive error message
                const parts = [];

                if (data?.message) {
                    parts.push(`Message: ${data.message}`);
                }

                if (data?.exception) {
                    parts.push(`Exception: ${data.exception}`);
                }

                if (data?.innerException) {
                    parts.push(`Inner Exception: ${data.innerException}`);
                }

                if (data?.details) {
                    parts.push(`Details: ${data.details}`);
                }

                if (data?.exceptionType) {
                    parts.push(`Exception Type: ${data.exceptionType}`);
                }

                if (data?.sqlState || data?.errorNumber) {
                    parts.push(`SQL State: ${data.sqlState}, Error Number: ${data.errorNumber}`);
                }

                const errorMsg = parts.length > 0 ? parts.join('\n\n') : JSON.stringify(data, null, 2);
                console.log('Error message to display:', errorMsg);
                setError(errorMsg);
                setIsBuilderCollapsed(false);  // Keep expanded on error
                setLoading(false);
                return;
            }

            setResults(response);
            setIsBuilderCollapsed(true);  // Auto-collapse on success
        } catch (err: any) {
            console.error('Query execution error - full error object:', err);
            console.error('Error response:', err.response);
            console.error('Error response data:', err.response?.data);
            console.error('Error message:', err.message);

            // Extract detailed error message
            let errorMessage = 'An error occurred while executing the query.';

            if (err.response) {
                // HTTP error response
                const data = err.response.data;
                console.log('Error data type:', typeof data);
                console.log('Error data:', data);

                if (typeof data === 'string') {
                    errorMessage = data;
                } else if (data) {
                    // Build comprehensive error message
                    const parts = [];

                    if (data.message && data.message !== 'Query Execution Error') {
                        parts.push(`Message: ${data.message}`);
                    }

                    if (data.exception) {
                        parts.push(`Exception: ${data.exception}`);
                    }

                    if (data.innerException) {
                        parts.push(`Inner Exception: ${data.innerException}`);
                    }

                    if (data.details) {
                        parts.push(`Details: ${data.details}`);
                    }

                    if (data.exceptionType) {
                        parts.push(`Exception Type: ${data.exceptionType}`);
                    }

                    if (data.sqlState || data.errorNumber) {
                        parts.push(`SQL State: ${data.sqlState}, Error Number: ${data.errorNumber}`);
                    }

                    console.log('Extracted error parts:', parts);
                    errorMessage = parts.length > 0 ? parts.join('\n\n') : JSON.stringify(data, null, 2);
                }
            } else if (err.message) {
                errorMessage = err.message;
            }

            console.log('Final error message to display:', errorMessage);
            setError(errorMessage);
            setIsBuilderCollapsed(false);  // Keep expanded on error
        } finally {
            setLoading(false);
        }
    };

    const tableOptions: IDropdownOption[] = Array.isArray(schema) ? schema.map(t => ({
        key: t.tableName,
        text: t.tableName
    })) : [];

    const currentTable = schema.find(t => t.tableName === selectedTable);

    const canExecute = selectedTable &&
        (isAggregationMode
            ? (groupByColumns.length > 0 || aggregations.length > 0)
            : selectedColumns.length > 0);

    return (
        <Stack tokens={{ childrenGap: 20 }} styles={{ root: { padding: 20, maxWidth: 1400 } }}>
            {error && (
                <MessageBar
                    messageBarType={MessageBarType.error}
                    onDismiss={() => setError(null)}
                    isMultiline={true}
                    styles={{
                        root: {
                            marginBottom: 10
                        },
                        text: {
                            whiteSpace: 'pre-wrap',
                            wordBreak: 'break-word'
                        }
                    }}
                >
                    <Text styles={{ root: { fontWeight: 600, marginBottom: 5 } }}>Query Execution Error:</Text>
                    <Text>{error}</Text>
                    <Text styles={{ root: { marginTop: 10, fontStyle: 'italic', fontSize: 12 } }}>
                        Please review your query configuration and try again.
                    </Text>
                </MessageBar>
            )}

            <Stack tokens={{ childrenGap: 15 }}>
                {/* Collapsible Header - Always Visible when table is selected */}
                {selectedTable && (
                    <Stack
                        horizontal
                        horizontalAlign="space-between"
                        verticalAlign="center"
                        styles={{
                            root: {
                                padding: '10px 15px',
                                backgroundColor: '#f3f2f1',
                                borderRadius: 4,
                                cursor: 'pointer'
                            }
                        }}
                        onClick={() => setIsBuilderCollapsed(!isBuilderCollapsed)}
                    >
                        <Stack horizontal tokens={{ childrenGap: 10 }} verticalAlign="center">
                            <IconButton
                                iconProps={{ iconName: isBuilderCollapsed ? 'ChevronRight' : 'ChevronDown' }}
                                title={isBuilderCollapsed ? "Expand Query Builder" : "Collapse Query Builder"}
                            />
                            <Text variant="mediumPlus" styles={{ root: { fontWeight: 600 } }}>
                                {isBuilderCollapsed ? 'Query Configuration' : 'SQL Query Builder'}
                            </Text>
                            {isBuilderCollapsed && results && (
                                <Text variant="small" styles={{ root: { color: '#605e5c', marginLeft: 10 } }}>
                                    Table: {selectedTable} |
                                    {' '}Columns: {isAggregationMode
                                        ? `${groupByColumns.length} grouped, ${aggregations.length} aggregated`
                                        : `${selectedColumns.length} selected`} |
                                    {' '}Filters: {filters.length} |
                                    {' '}Sorting: {sortOrders.length}
                                </Text>
                            )}
                        </Stack>
                        {isBuilderCollapsed && (
                            <Stack horizontal tokens={{ childrenGap: 10 }}>
                                <DefaultButton
                                    text="New Query"
                                    iconProps={{ iconName: 'Add' }}
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        handleNewQuery();
                                    }}
                                />
                                <PrimaryButton
                                    text="Re-run Query"
                                    iconProps={{ iconName: 'Refresh' }}
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        handleExecuteQuery();
                                    }}
                                    disabled={!canExecute || loading}
                                />
                            </Stack>
                        )}
                    </Stack>
                )}

                {/* Table Dropdown - Always show when no table selected */}
                {!selectedTable && (
                    <Dropdown
                        label="Select Table"
                        placeholder="Choose a table"
                        options={tableOptions}
                        selectedKey={selectedTable}
                        onChange={handleTableChange}
                        required
                        styles={{ dropdown: { width: 300 } }}
                    />
                )}

                {/* Collapsible Builder Section - Only when table selected and not collapsed */}
                {selectedTable && !isBuilderCollapsed && (
                    <>
                        <Toggle
                            label="Aggregation Mode"
                            inlineLabel
                            checked={isAggregationMode}
                            onChange={handleAggregationModeChange}
                            onText="On"
                            offText="Off"
                        />

                        <Stack horizontal tokens={{ childrenGap: 15 }} verticalAlign="end" styles={{ root: { marginTop: 10, marginBottom: 10 } }}>
                            <DefaultButton
                                text="New Query"
                                iconProps={{ iconName: 'Add' }}
                                onClick={handleNewQuery}
                            />
                            <SpinButton
                                label="Max Rows"
                                value={maxRows.toString()}
                                min={1}
                                max={10000}
                                step={100}
                                onChange={(_, newValue) => {
                                    const value = parseInt(newValue || '1000');
                                    setMaxRows(Math.min(Math.max(value, 1), 10000));
                                }}
                                styles={{ root: { width: 150 } }}
                            />
                            <PrimaryButton
                                text={loading ? "Executing..." : "Execute Query"}
                                onClick={handleExecuteQuery}
                                disabled={!canExecute || loading}
                            />
                            {loading && <Spinner />}
                        </Stack>

                        {currentTable && (
                            <ColumnListWithActions
                                columns={currentTable.columns}
                                isAggregationMode={isAggregationMode}
                                selectedColumns={selectedColumns}
                                filters={filters}
                                groupByColumns={groupByColumns}
                                dateGroupings={dateGroupings}
                                aggregations={aggregations}
                                sortOrders={sortOrders}
                                onSelectedColumnsChange={setSelectedColumns}
                                onFiltersChange={setFilters}
                                onGroupByColumnsChange={setGroupByColumns}
                                onDateGroupingsChange={setDateGroupings}
                                onAggregationsChange={setAggregations}
                                onSortOrdersChange={setSortOrders}
                            />
                        )}

                    </>
                )}
            </Stack>

            {results && (
                <>
                    <Stack tokens={{ childrenGap: 10 }}>
                        <Stack horizontal tokens={{ childrenGap: 10 }} verticalAlign="center">
                            <DefaultButton
                                text={showSql ? "Hide SQL" : "Show Generated SQL"}
                                iconProps={{ iconName: showSql ? 'ChevronUp' : 'ChevronDown' }}
                                onClick={() => setShowSql(!showSql)}
                            />
                            {showSql && results.generatedSql && (
                                <IconButton
                                    iconProps={{ iconName: 'Copy' }}
                                    title="Copy SQL to clipboard"
                                    ariaLabel="Copy SQL"
                                    onClick={() => {
                                        navigator.clipboard.writeText(results.generatedSql);
                                    }}
                                />
                            )}
                        </Stack>

                        {showSql && results.generatedSql && (
                            <Stack
                                styles={{
                                    root: {
                                        padding: 15,
                                        backgroundColor: '#f3f2f1',
                                        border: '1px solid #edebe9',
                                        borderRadius: 4,
                                        fontFamily: 'Consolas, Monaco, monospace',
                                        fontSize: 13,
                                        whiteSpace: 'pre-wrap',
                                        wordBreak: 'break-all',
                                        overflowX: 'auto'
                                    }
                                }}
                            >
                                <Text>{results.generatedSql}</Text>
                            </Stack>
                        )}
                    </Stack>

                    <ResultsTable
                        results={results}
                        tableName={selectedTable}
                    />
                </>
            )}
        </Stack>
    );
};

export default QueryBuilder;
